/**
 * Tour Callout pointer: the callout edge its arrow sits on. When the callout is anchored, it
 * is placed on the opposite side of the target (pointer `top` → callout below the target).
 */
export type EsnTourPointer = 'none' | 'top' | 'bottom' | 'left' | 'right';

/**
 * One question of a Help Center FAQ subject.
 */
export interface EsnFaqEntry {
  id: string;
  question: string;
  answer: string;
}

/**
 * Feedback Form kinds offered by default (Figma "Segmented / Feedback Kind").
 */
export type EsnFeedbackKind = 'problem' | 'idea' | 'question';

/**
 * What the Send Feedback form hands to the consumer on submit.
 */
export interface EsnFeedbackPayload {
  kind: string;
  subject: string;
  details: string;
}

/**
 * One support channel in the Talk to Us card (email address, phone number, …).
 */
export interface EsnContactChannel {
  /**
   * Small caps label above the value, e.g. "Email".
   */
  label: string;
  value: string;

  /**
   * Link target; defaults to `mailto:` / `tel:` for kind `email` / `phone`.
   */
  href?: string;
  kind?: 'email' | 'phone' | 'link';
  note?: string;
}

/**
 * One label/value fact support asks for (signed-in user, organisation, scope).
 */
export interface EsnContactFact {
  label: string;
  value: string;
}

/**
 * Resolves a contact channel's link target.
 */
export function esnContactHref(channel: EsnContactChannel): string | null {
  if (channel.href) return channel.href;
  if (channel.kind === 'email') return `mailto:${channel.value}`;
  if (channel.kind === 'phone') return `tel:${channel.value.replace(/[^\d+]/g, '')}`;
  return null;
}

/**
 * Gap between an anchored Tour Callout and its target, and its minimum distance to the viewport edge.
 */
export const ESN_TOUR_GAP = 12;
export const ESN_TOUR_VIEWPORT_MARGIN = 8;

/**
 * Where a Tour Callout progress dot stands relative to the current step.
 */
export type EsnTourDot = 'done' | 'current' | 'todo';

/**
 * Viewport position (for `position: fixed`) of a callout anchored to `target`: on the side
 * opposite its pointer, centred on the target, kept inside the viewport.
 */
export function esnTourPlacement(
  target: { top: number; left: number; width: number; height: number },
  callout: { width: number; height: number },
  pointer: EsnTourPointer,
  viewport: { width: number; height: number },
): { top: number; left: number } {
  const centreX = target.left + target.width / 2 - callout.width / 2;
  const centreY = target.top + target.height / 2 - callout.height / 2;
  let top: number;
  let left: number;
  switch (pointer) {
    case 'bottom':
      top = target.top - callout.height - ESN_TOUR_GAP;
      left = centreX;
      break;
    case 'left':
      top = centreY;
      left = target.left + target.width + ESN_TOUR_GAP;
      break;
    case 'right':
      top = centreY;
      left = target.left - callout.width - ESN_TOUR_GAP;
      break;
    default:
      top = target.top + target.height + ESN_TOUR_GAP;
      left = centreX;
  }
  const clamp = (value: number, size: number, max: number) =>
    Math.max(ESN_TOUR_VIEWPORT_MARGIN, Math.min(value, max - size - ESN_TOUR_VIEWPORT_MARGIN));
  return {
    top: Math.round(clamp(top, callout.height, viewport.height)),
    left: Math.round(clamp(left, callout.width, viewport.width)),
  };
}
